import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { querySuites } from '@/features/suites/data/suites';
import { resolveSuiteEntityDisplayName } from '@/features/suites/utils/suiteDisplayName';
import { useIsAuthenticated } from '@/shared/hooks/auth';
import { queryKeys } from '@/shared/hooks/queryKeys';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { ApiError } from '@/shared/data/client';
import type { SuiteListItemDto } from '@flowqa/shared';

type SuiteOption = TableDropdownOption;
type SuiteOptionRecord = {
  value: string;
  projectId: string;
  rawName: string;
  rawDisplayName: string | null;
};

type UseSuiteOptionsParams = {
  projectId?: string | null;
  enabled?: boolean;
};

type UseSuiteOptionsResult = { suiteOptions: SuiteOption[]; loading: boolean };

const SUITE_OPTIONS_PAGE_SIZE = 100;

const queryAllSuiteOptions = async (
  projectId: string | null | undefined,
  organisationId: string | undefined,
): Promise<SuiteOptionRecord[]> => {
  const items: SuiteListItemDto[] = [];
  let page = 1;
  let total = 0;

  do {
    const result = await querySuites(projectId, organisationId, {
      page,
      pageSize: SUITE_OPTIONS_PAGE_SIZE,
      sorts: [{ field: 'name', direction: 'asc' }],
      filters: [],
    });
    items.push(...result.items);
    total = result.total;
    page += 1;
  } while (items.length < total);

  return items.map((suite) => ({
    value: suite.id,
    projectId: suite.projectId,
    rawName: suite.name,
    rawDisplayName: suite.displayName ?? null,
  }));
};

export const useSuiteOptions = ({
  projectId,
  enabled = true,
}: UseSuiteOptionsParams = {}): UseSuiteOptionsResult => {
  const isAuthenticated = useIsAuthenticated();
  const { preferFileNameOverDisplayName } = useProjectStatusPresentation();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const suiteOptionsQuery: UseQueryResult<SuiteOptionRecord[], ApiError> = useQuery<
    SuiteOptionRecord[],
    ApiError
  >({
    queryKey: queryKeys.suiteOptions(projectId, orgKey),
    enabled: isAuthenticated && enabled,
    queryFn: async () => await queryAllSuiteOptions(projectId, selectedOrganisationId ?? undefined),
    staleTime: 30_000,
  });

  const suiteOptions = useMemo(
    () =>
      (suiteOptionsQuery.data ?? [])
        .map((suite) => ({
          ...suite,
          label: resolveSuiteEntityDisplayName(
            { name: suite.rawName, displayName: suite.rawDisplayName },
            {
              preferFileNameOverDisplayName,
            },
          ),
        }))
        .sort((left, right) => left.label.localeCompare(right.label))
        .map(({ value, label, projectId: optionProjectId }) => ({
          value,
          label,
          projectId: optionProjectId,
        })),
    [preferFileNameOverDisplayName, suiteOptionsQuery.data],
  );

  return {
    suiteOptions,
    loading: suiteOptionsQuery.isLoading,
  };
};
